import { formatPercent } from "./utils";

export type GoalType = "expense" | "inheritance" | "asset_sale";
export type GoalInflowTaxTreatment =
  | "ordinary_income"
  | "short_term_capital_gain"
  | "long_term_capital_gain";
export type GoalYearReference =
  | "calendar_year"
  | "retirement"
  | "death"
  | "age"
  | "first_death";

export interface EventYearReferencePerson {
  id: string;
  type: "primary" | "spouse";
  firstName: string | null;
  lastName: string | null;
  birthYear: number | null;
  deathYear: number | null;
  targetFireYear: number | null;
}

export interface EventYearReferenceInput {
  ref: GoalYearReference;
  year: number | null | undefined;
  personId?: string | null;
  age?: number | null | undefined;
}

const DEFAULT_PURCHASE_GROWTH_RATE = 0.015;

export const DEFAULT_PURCHASE_GROWTH_RATE_PERCENT = String(
  DEFAULT_PURCHASE_GROWTH_RATE * 100,
);

export const EVENT_DESCRIPTION_PLACEHOLDERS: Record<GoalType, string> = {
  expense: "Description (e.g. Buy a house)",
  inheritance: "Description (e.g. Private stock IPO)",
  asset_sale: "Description (e.g. Sell vacation home)",
};

export const LIFE_EVENT_TYPE_LABELS: Record<GoalType, string> = {
  expense: "Expense",
  inheritance: "Windfall / Liquidity Event",
  asset_sale: "Windfall / Liquidity Event",
};

export const LIFE_EVENT_TYPE_HINTS: Record<GoalType, string> = {
  expense: "One-time or repeating expense like a house, car, or renovation",
  inheritance:
    "One-time money coming in, like an inheritance, private stock IPO, asset liquidation, or other windfall",
  asset_sale:
    "One-time money coming in, like an inheritance, private stock IPO, asset liquidation, or other windfall",
};

export const LIFE_EVENT_TAX_TREATMENT_LABELS: Record<
  GoalInflowTaxTreatment,
  string
> = {
  ordinary_income: "Income",
  short_term_capital_gain: "Short-term cap gain",
  long_term_capital_gain: "Long-term cap gain",
};

export function getPersonLabel(person: EventYearReferencePerson): string {
  const first = person.firstName?.trim();
  const last = person.lastName?.trim();
  const fullName = [first, last].filter(Boolean).join(" ");
  if (fullName) return fullName;
  return person.type === "spouse" ? "Partner" : "You";
}

export function parseAmount(raw: string): number | undefined | null {
  if (!raw.trim()) return undefined;
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseYear(raw: string): number | null {
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function parseRepeatInterval(raw: string): number | null {
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : null;
}

export function parsePercent(raw: string): number | null {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed / 100 : null;
}

export function resolveEventYearReference(
  input: EventYearReferenceInput,
  profiles: EventYearReferencePerson[],
  currentFireYear: number | null | undefined,
): number | null {
  if (input.ref === "calendar_year") return input.year ?? null;

  if (input.ref === "first_death") {
    const deathYears = profiles
      .map((profile) => profile.deathYear)
      .filter((year): year is number => year != null);
    return deathYears.length > 0 ? Math.min(...deathYears) : null;
  }

  const person = profiles.find((profile) => profile.id === input.personId);
  if (!person) return null;

  if (input.ref === "retirement")
    return person.targetFireYear ?? currentFireYear ?? null;
  if (input.ref === "death") return person.deathYear ?? null;
  if (input.ref === "age") {
    if (person.birthYear == null || input.age == null) return null;
    return person.birthYear + input.age;
  }

  return input.year ?? null;
}

export function formatYearReferenceLabel(
  input: EventYearReferenceInput,
  profiles: EventYearReferencePerson[],
  currentFireYear: number | null | undefined,
): string {
  const resolvedYear = resolveEventYearReference(
    input,
    profiles,
    currentFireYear,
  );

  if (input.ref === "calendar_year") {
    return resolvedYear != null ? String(resolvedYear) : "Calendar year";
  }

  if (input.ref === "first_death") {
    return resolvedYear != null
      ? `At First Death (${resolvedYear})`
      : "At First Death";
  }

  const person = profiles.find((profile) => profile.id === input.personId);
  const personLabel = person ? getPersonLabel(person) : "Selected person";

  if (input.ref === "retirement") {
    if (resolvedYear == null) return `${personLabel}'s Retirement`;
    const maybeAge =
      person?.birthYear != null ? resolvedYear - person.birthYear : null;
    return maybeAge != null
      ? `${personLabel}'s Retirement (age ${maybeAge} in ${resolvedYear})`
      : `${personLabel}'s Retirement (${resolvedYear})`;
  }

  if (input.ref === "death") {
    return resolvedYear != null
      ? `${personLabel}'s Death (${resolvedYear})`
      : `${personLabel}'s Death`;
  }

  if (input.ref === "age") {
    if (input.age == null) return `${personLabel} at age`;
    return resolvedYear != null
      ? `${personLabel} at age ${input.age} (${resolvedYear})`
      : `${personLabel} at age ${input.age}`;
  }

  return resolvedYear != null ? String(resolvedYear) : "Year";
}

export function formatRecurrence(
  repeatEveryYears: number | null | undefined,
  growthRate: number | string | null | undefined,
  endYear?: number | null,
): string | null {
  if (repeatEveryYears == null) return null;
  const cadence = `Repeats every ${repeatEveryYears} year${repeatEveryYears === 1 ? "" : "s"}`;
  const endYearLabel = endYear != null ? ` through ${endYear}` : "";
  return `${cadence}${endYearLabel} at ${formatPercent(Number(growthRate ?? 0), 1)} growth`;
}

export function getDefaultRecurringEndYear(
  startYearRaw: string,
  defaultPlanEndYear: number,
): string {
  const parsedStartYear = parseYear(startYearRaw);
  if (parsedStartYear == null) return String(defaultPlanEndYear);
  return String(Math.max(defaultPlanEndYear, parsedStartYear));
}

// ─── Year reference picker state ─────────────────────────────────────────────

export interface YearReferencePickerValue {
  ref: GoalYearReference;
  year: string;
  personId: string;
  age: string;
}

// ─── Goal event shape (matches tRPC fire.listEvents response rows) ───────────

export type GoalEvent = {
  id: string;
  type: GoalType;
  description: string;
  year: number;
  startYearRef: GoalYearReference;
  startYearPersonId: string | null;
  startYearAge: number | null;
  amount: number | string | null;
  repeatEveryYears: number | null;
  endYear: number | null;
  endYearRef: GoalYearReference;
  endYearPersonId: string | null;
  endYearAge: number | null;
  growthRate: number | string | null;
  budgetCategoryId?: number | null;
  isTaxable?: boolean | null;
  taxTreatment?: GoalInflowTaxTreatment | null;
};

// ─── Parsed year reference output ────────────────────────────────────────────

export type ParsedYearReferenceInput = {
  year: number;
  ref: GoalYearReference;
  personId?: string;
  age?: number;
  resolvedYear: number | null;
};

// ─── Values produced by the form after parsing ───────────────────────────────

export interface LifeEventFormValues {
  type: GoalType;
  description: string;
  amount: number | undefined;
  year: number;
  startYearRef: GoalYearReference;
  startYearPersonId?: string;
  startYearAge?: number;
  repeatEveryYears?: number;
  endYear?: number;
  endYearRef?: GoalYearReference;
  endYearPersonId?: string;
  endYearAge?: number;
  growthRate?: number;
  isTaxable?: boolean;
  taxTreatment?: GoalInflowTaxTreatment | null;
  /** Expense events only. Null = deliberate "no specific category". */
  budgetCategoryId?: number | null;
}

type BaseLifeEventMutationInput = {
  description: string;
  amount: number | undefined;
  year: number;
  startYearRef: GoalYearReference;
  startYearPersonId?: string;
  startYearAge?: number;
};

type ExpenseLifeEventMutationInput = BaseLifeEventMutationInput & {
  type: "expense";
  repeatEveryYears?: number;
  endYear?: number;
  endYearRef?: GoalYearReference;
  endYearPersonId?: string;
  endYearAge?: number;
  growthRate?: number;
  budgetCategoryId?: number | null;
};

type InflowLifeEventMutationInput = BaseLifeEventMutationInput & {
  type: Exclude<GoalType, "expense">;
  isTaxable: boolean;
  taxTreatment: GoalInflowTaxTreatment | null;
};

export type CreateLifeEventMutationInput =
  | ExpenseLifeEventMutationInput
  | InflowLifeEventMutationInput;

export type UpdateLifeEventMutationInput = CreateLifeEventMutationInput & {
  id: string;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

export function createYearReferenceState(year: string): YearReferencePickerValue {
  return { ref: "calendar_year", year, personId: "", age: "" };
}

export function isLifeEventInflowType(type: GoalType): boolean {
  return type === "inheritance" || type === "asset_sale";
}

export function normalizeLifeEventTaxTreatment(
  isTaxable: boolean | null | undefined,
  taxTreatment: GoalInflowTaxTreatment | null | undefined,
): GoalInflowTaxTreatment | null {
  if (!isTaxable) return null;
  return taxTreatment ?? "ordinary_income";
}

function buildBaseLifeEventMutationInput(
  values: LifeEventFormValues,
): BaseLifeEventMutationInput {
  return {
    description: values.description,
    amount: values.amount,
    year: values.year,
    startYearRef: values.startYearRef,
    ...(values.startYearPersonId
      ? { startYearPersonId: values.startYearPersonId }
      : {}),
    ...(values.startYearAge != null ? { startYearAge: values.startYearAge } : {}),
  };
}

export function toCreateLifeEventMutationInput(
  values: LifeEventFormValues,
): CreateLifeEventMutationInput {
  const base = buildBaseLifeEventMutationInput(values);

  if (values.type === "expense") {
    return {
      ...base,
      type: "expense",
      ...(values.budgetCategoryId !== undefined
        ? { budgetCategoryId: values.budgetCategoryId }
        : {}),
      ...(values.repeatEveryYears != null
        ? {
            repeatEveryYears: values.repeatEveryYears,
            endYear: values.endYear,
            ...(values.endYearRef != null ? { endYearRef: values.endYearRef } : {}),
            ...(values.endYearPersonId ? { endYearPersonId: values.endYearPersonId } : {}),
            ...(values.endYearAge != null ? { endYearAge: values.endYearAge } : {}),
            growthRate: values.growthRate,
          }
        : {}),
    };
  }

  const taxTreatment = normalizeLifeEventTaxTreatment(
    values.isTaxable,
    values.taxTreatment,
  );

  return {
    ...base,
    type: values.type,
    isTaxable: taxTreatment != null,
    taxTreatment,
  };
}

export function toUpdateLifeEventMutationInput(
  id: string,
  values: LifeEventFormValues,
): UpdateLifeEventMutationInput {
  return {
    id,
    ...toCreateLifeEventMutationInput(values),
  };
}

export function formatLifeEventTaxLabel(event: {
  type: GoalType;
  isTaxable?: boolean | null;
  taxTreatment?: GoalInflowTaxTreatment | null;
}): string | null {
  if (!isLifeEventInflowType(event.type)) return null;
  const treatment = normalizeLifeEventTaxTreatment(
    event.isTaxable,
    event.taxTreatment,
  );
  if (treatment == null) return "Not taxable";
  return `Taxable: ${LIFE_EVENT_TAX_TREATMENT_LABELS[treatment]}`;
}

export function eventToYearReferenceState(
  event: GoalEvent,
  kind: "start" | "end",
  fallbackYear: string,
): YearReferencePickerValue {
  if (kind === "start") {
    return {
      ref: event.startYearRef ?? "calendar_year",
      year: String(event.year),
      personId: event.startYearPersonId ?? "",
      age: event.startYearAge != null ? String(event.startYearAge) : "",
    };
  }
  return {
    ref: event.endYearRef ?? "calendar_year",
    year: event.endYear != null ? String(event.endYear) : fallbackYear,
    personId: event.endYearPersonId ?? "",
    age: event.endYearAge != null ? String(event.endYearAge) : "",
  };
}

export function parseYearReferenceInput(args: {
  label: "Start" | "End";
  value: YearReferencePickerValue;
  fallbackYear: number;
  profiles: EventYearReferencePerson[];
  currentFireYear: number | null | undefined;
}): { parsed: ParsedYearReferenceInput | null; error: string | null } {
  const parsedCalendarYear = parseYear(args.value.year);
  const parsedAge = args.value.ref === "age" ? parseYear(args.value.age) : null;

  if (args.value.ref === "calendar_year" && parsedCalendarYear == null) {
    return { parsed: null, error: `${args.label} year is required.` };
  }
  if (
    (args.value.ref === "retirement" ||
      args.value.ref === "death" ||
      args.value.ref === "age") &&
    !args.value.personId
  ) {
    return { parsed: null, error: `${args.label} year person is required.` };
  }
  if (args.value.ref === "age" && parsedAge == null) {
    return { parsed: null, error: `${args.label} age is required.` };
  }

  const resolvedYear = resolveEventYearReference(
    {
      ref: args.value.ref,
      year: parsedCalendarYear ?? args.fallbackYear,
      personId: args.value.personId || null,
      age: parsedAge,
    },
    args.profiles,
    args.currentFireYear,
  );

  return {
    parsed: {
      year: resolvedYear ?? parsedCalendarYear ?? args.fallbackYear,
      ref: args.value.ref,
      ...(args.value.personId ? { personId: args.value.personId } : {}),
      ...(parsedAge != null ? { age: parsedAge } : {}),
      resolvedYear,
    },
    error: null,
  };
}
